<template>
  <div class="item-detail-panel">
    <!-- NFT metadata -->
    <template v-if="itemType === 'nft'">
      <!-- Collection name -->
      <div v-if="item.collection" class="detail-row">
        <span class="detail-value collection-name">{{ item.collection }}</span>
      </div>
      <!-- All NFT meta in a single inline row -->
      <div class="detail-row inline-meta">
        <template v-if="item.contractAddress">
          <span class="detail-label">Contract:</span>
          <span class="detail-value mono">{{ formatAddress(item.contractAddress) }}</span>
        </template>
        <template v-if="item.tokenId">
          <span v-if="item.contractAddress" class="bullet">·</span>
          <span class="detail-label">Token:</span>
          <span class="detail-value">{{ item.tokenId }}</span>
        </template>
        <template v-if="item.standard">
          <span class="bullet">·</span>
          <span class="detail-value">{{ item.standard }}</span>
        </template>
        <template v-if="item.chain">
          <span class="bullet">·</span>
          <a v-if="item.explorerUrl" :href="item.explorerUrl" target="_blank" rel="noopener noreferrer" class="detail-value link">
            <span class="chain-badge">{{ item.chain }}</span>
            <svg class="external-link-icon" viewBox="0 0 500 500" aria-hidden="true">
              <use :href="`${iconsetPath}#externalLink-tiny`"></use>
            </svg>
          </a>
          <span v-else class="detail-value"><span class="chain-badge">{{ item.chain }}</span></span>
        </template>
        <template v-for="(link, idx) in nftLinks" :key="link.label">
          <span class="bullet">·</span>
          <a
            :href="link.url"
            target="_blank"
            rel="noopener noreferrer"
            class="nft-external-link"
          >
            {{ link.label }}
            <svg class="external-link-icon" viewBox="0 0 500 500" aria-hidden="true">
              <use :href="`${iconsetPath}#externalLink-tiny`"></use>
            </svg>
          </a>
        </template>
      </div>
      <!-- Attributes grid (filtered: removes Unit-injected traits) -->
      <div
        v-if="filteredAttributes.length"
        class="nft-attributes-grid"
      >
        <div
          v-for="attr in filteredAttributes"
          :key="attr.trait_type || attr.key"
          class="nft-attribute-pill"
        >
          <span class="nft-attribute-trait">{{
            attr.trait_type || attr.key
          }}</span>
          <span class="nft-attribute-value">{{ attr.value }}</span>
        </div>
      </div>
    </template>

    <!-- Product metadata -->
    <template v-else-if="itemType === 'product'">
      <div v-if="formattedPrice" class="detail-row">
        <span class="detail-label">Price</span>
        <span class="detail-value"
          >{{ formattedPrice }} {{ displayCoinSymbol }}</span
        >
      </div>
      <div v-if="item.existingStock !== undefined" class="detail-row">
        <span class="detail-label">Stock</span>
        <span class="detail-value">{{ item.existingStock }}</span>
      </div>
      <div v-if="item.condition" class="detail-row">
        <span class="detail-label">Condition</span>
        <span class="detail-value">{{ item.condition }}</span>
      </div>
      <div v-if="item.category" class="detail-row">
        <span class="detail-label">Category</span>
        <span class="detail-value">{{ item.category }}</span>
      </div>
    </template>

    <!-- Service metadata -->
    <template v-else-if="itemType === 'service'">
      <div v-if="formattedPrice" class="detail-row">
        <span class="detail-label">Price</span>
        <span class="detail-value"
          >{{ formattedPrice }} {{ displayCoinSymbol }}</span
        >
      </div>
      <div v-if="item.category" class="detail-row">
        <span class="detail-label">Category</span>
        <span class="detail-value">{{ item.category }}</span>
      </div>
      <div v-if="item.serviceDuration" class="detail-row">
        <span class="detail-label">Duration</span>
        <span class="detail-value">{{ item.serviceDuration }} min</span>
      </div>
      <div v-if="item.tags && item.tags.length" class="detail-row">
        <span class="detail-label">Tags</span>
        <span class="detail-value">{{ item.tags.join(", ") }}</span>
      </div>
    </template>
  </div>
</template>

<script>
import { computed } from "vue";
import iconsetPath from "@/assets/icons/iconset.svg";

// Unit-injected on-chain traits that duplicate info already shown in the detail panel
const UNIT_INJECTED_TRAITS = [
  "Coin",
  "Price",
  "Category",
  "Condition",
  "Media Type",
];

export default {
  name: "ItemDetailPanel",
  props: {
    item: {
      type: Object,
      required: true,
    },
    itemType: {
      type: String,
      required: true,
      validator: (v) => ["nft", "product", "service"].includes(v),
    },
    formattedPrice: {
      type: String,
      default: "",
    },
    displayCoinSymbol: {
      type: String,
      default: "",
    },
    isOwner: {
      type: Boolean,
      default: false,
    },
    nftLinks: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const filteredAttributes = computed(() => {
      if (!props.item?.attributes?.length) return [];
      return props.item.attributes.filter(
        (a) => !UNIT_INJECTED_TRAITS.includes(a.trait_type),
      );
    });

    const formatAddress = (addr) => {
      if (!addr || addr.length < 10) return addr || "";
      return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
    };

    const isIPFSUrl = (url) => {
      if (!url) return false;
      return (
        url.startsWith("ipfs://") ||
        url.includes("/ipfs/") ||
        url.includes("ipfs.io")
      );
    };

    return {
      filteredAttributes,
      formatAddress,
      isIPFSUrl,
      iconsetPath,
    };
  },
};
</script>

<style scoped>
.item-detail-panel {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-label, 0.85rem);
  line-height: 1.4;
}

.detail-label {
  color: var(--color-text-muted);
  min-width: 80px;
  flex-shrink: 0;
}

.inline-meta .detail-label {
  min-width: auto;
  font-weight: 500;
}

.detail-value {
  color: var(--color-text-muted);
}

.detail-value.mono {
  font-family: monospace;
  font-size: 0.8rem;
}

.detail-value.link {
  color: #0066ff;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: var(--space-1, 4px);
  transition: opacity 0.15s ease;
}

.detail-value.link:hover {
  opacity: 0.75;
}

.collection-name {
  font-weight: 500;
  color: var(--color-text-muted);
  font-size: var(--font-size-label, 0.85rem);
}

.inline-meta {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px;
}

.bullet {
  color: var(--color-text-muted);
  user-select: none;
  padding: 0 2px;
}

.chain-badge {
  text-transform: capitalize;
  font-weight: 500;
}

/* On-chain links — matches LinkifiedText purple style */
.nft-external-link {
  color: var(--color-accent-primary);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: var(--font-size-label, 0.85rem);
}

.nft-external-link:hover {
  text-decoration: underline;
}

.nft-external-link .external-link-icon {
  width: 0.85em;
  height: 0.85em;
  display: inline;
  stroke: currentColor;
  stroke-width: 22;
  fill: none;
}

/* NFT Attributes grid */
.nft-attributes-grid {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1, 6px);
  padding-top: var(--space-1, 4px);
}

.nft-attribute-pill {
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: var(--color-surface-canvas, #fff);
  border-radius: var(--radius-sm, 12px);
  padding: var(--space-1, 6px) var(--space-2, 10px);
  min-width: 80px;
}

.nft-attribute-trait {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-text-muted);
}

.nft-attribute-value {
  font-size: var(--font-size-label, 0.85rem);
  color: var(--color-text-primary, #373737);
  font-weight: 500;
}
</style>
